import React, { useEffect, useState, useRef } from "react";
import { useAppContext } from "../../contexts/AppContext";
import axios from "axios";
import parse from "html-react-parser";

const Counter = ({ text }) => {
  const [count, setCount] = useState(0);
  const [target, setTarget] = useState(0);
  const [suffix, setSuffix] = useState("");
  const [prefix, setPrefix] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = counterRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  useEffect(() => {
    // Extract number and surrounding characters
    const match = text.match(/(\d+)/);
    if (match) {
      const num = parseInt(match[0], 10);
      setTarget(num);
      const parts = text.split(match[0]);
      setPrefix(parts[0] || "");
      setSuffix(parts[1] || "");
    } else {
      setTarget(0);
      setSuffix(text);
    }
  }, [text]);

  useEffect(() => {
    if (!isVisible) return;

    let start = 0;
    const end = target;
    if (start === end) return;

    // Increased duration to 3 seconds for a slower animation
    let totalDuration = 3000;
    let startTime = null;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / totalDuration, 1);

      // Using easeOutExpo for a smoother premium feel
      const easeOutExpo = (x) => (x === 1 ? 1 : 1 - Math.pow(2, -10 * x));
      const easedProgress = easeOutExpo(progress);

      setCount(Math.floor(easedProgress * end));

      if (progress < 1) {
        window.requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    window.requestAnimationFrame(animate);
  }, [target, isVisible]);

  return (
    <span ref={counterRef}>
      {prefix}
      {count}
      {suffix}
    </span>
  );
};

function Statics() {
  const [staticsData, setStaticsData] = useState([]);
  const { backendUrl, translate } = useAppContext();
  const staticsRoute = "/statics";

  const healthIcons = [
    { icon: "bi-heart-pulse-fill" },
    { icon: "bi-shield-plus" },
    { icon: "bi-person-hearts" },
    { icon: "bi-activity" },
    { icon: "bi-hospital" },
    { icon: "bi-capsule-pill" },
    { icon: "bi-bandaid" },
    { icon: "bi-clipboard2-pulse-fill" },
  ];

  useEffect(() => {
    const fetchStaticsData = async () => {
      try {
        const response = await axios.get(`${backendUrl}${staticsRoute}`);
        if (Array.isArray(response.data)) {
          setStaticsData(response.data);
        }
      } catch (error) {
        console.error("Error fetching statics data:", error);
      }
    };
    fetchStaticsData();
  }, [backendUrl]);

  return (
    <div>
      {Array.isArray(staticsData) && staticsData.length > 0 && (
        <div className="statics-container animated">
          <div className="p-1 d-flex w-100 justify-content-around flex-wrap">
            {staticsData.map((stat, index) => {
              const iconConfig = healthIcons[index % healthIcons.length];
              return (
                <div key={index} className="m-3">
                  <div className="d-flex flex-column justify-content-center align-items-center">
                    <div
                      style={{
                        width: "72px",
                        height: "72px",
                        borderRadius: "50%",
                        background: "var(--primary-color)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "0.75rem",
                        transition: "transform 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.12) translateY(-5px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1) translateY(0)";
                      }}
                    >
                      <i
                        className={`bi ${iconConfig.icon}`}
                        style={{
                          fontSize: "2rem",
                          color: "white",
                          filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                        }}
                      />
                    </div>
                    <h2 className="fw-bold mb-0" style={{ color: "#1e293b" }}>
                      <Counter text={stat.title ? translate(stat.title) : ""} />
                    </h2>
                    <p
                      className="text-muted text-center"
                      style={{ fontWeight: 400, fontSize: "0.95rem", maxWidth: "200px" }}
                    >
                      {stat.content ? parse(translate(stat.content)) : ""}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default Statics;
